/** @module @liquidefy/code-editor/syntax/syntax-tokenizer @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  Token,
  TokenType,
  TokenizerState,
  TokenLine,
  GrammarPattern,
  GrammarCaptures,
  GrammarDefinition,
  Range,
  LanguageId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asLanguageId,
} from '../code-editor.js';

// ═══ §2 TOKENIZATION — line-by-line tokenization with scope tracking ═══

/**
 * Tokenize a single line of text given the current tokenizer state and grammar.
 * Returns tokens found on that line and the end-state for the next line.
 */
export function tokenizeLine(
  line: string,
  state: TokenizerState,
  grammar: GrammarDefinition,
): { tokens: Token[]; endState: TokenizerState } {
  const tokens: Token[] = [];
  let offset = 0;
  const ruleStack = [...state.ruleStack];
  const activePatterns = resolveActivePatterns(ruleStack, grammar);

  while (offset < line.length) {
    const result = findFirstMatch(line, offset, activePatterns);
    if (result === null) {
      tokens.push({
        type: scopeStackToTokenType(ruleStack),
        startOffset: offset,
        endOffset: line.length,
        scopes: [...ruleStack],
      });
      break;
    }

    if (result.offset > offset) {
      tokens.push({
        type: scopeStackToTokenType(ruleStack),
        startOffset: offset,
        endOffset: result.offset,
        scopes: [...ruleStack],
      });
    }

    if (result.pattern.begin !== undefined && result.pattern.end !== undefined) {
      const beginResult = applyBeginEnd(line, result.offset, result.pattern, {
        ruleStack,
        languageId: state.languageId,
      });
      for (const t of beginResult.tokens) {
        tokens.push(t);
      }
      offset = beginResult.newOffset;
      if (beginResult.pushedScope) {
        ruleStack.push(beginResult.pushedScope);
      }
    } else {
      const matchEnd = result.offset + result.matchLength;
      const capturedTokens = result.pattern.captures
        ? matchCaptures(line, { index: result.offset, length: result.matchLength, text: result.matchText }, result.pattern.captures)
        : [];

      if (capturedTokens.length > 0) {
        for (const ct of capturedTokens) {
          tokens.push(ct);
        }
      } else {
        const scopes = result.pattern.name
          ? [...ruleStack, result.pattern.name]
          : [...ruleStack];
        tokens.push({
          type: scopeToTokenType(scopes),
          startOffset: result.offset,
          endOffset: matchEnd,
          scopes,
        });
      }
      offset = matchEnd;
    }
  }

  if (tokens.length === 0) {
    tokens.push({
      type: 'whitespace' as TokenType,
      startOffset: 0,
      endOffset: line.length,
      scopes: [...ruleStack],
    });
  }

  return {
    tokens,
    endState: {
      ruleStack,
      languageId: state.languageId,
    },
  };
}

/**
 * Tokenize multiple lines starting from the initial state.
 */
export function tokenizeLines(
  lines: string[],
  grammar: GrammarDefinition,
): TokenLine[] {
  const result: TokenLine[] = [];
  let state = createInitialState(grammar.scopeName as unknown as LanguageId);

  for (let i = 0; i < lines.length; i++) {
    const lineResult = tokenizeLine(lines[i]!, state, grammar);
    result.push({
      lineNumber: i,
      tokens: lineResult.tokens,
      state,
      version: 1,
    });
    state = lineResult.endState;
  }

  return result;
}

/**
 * Incrementally retokenize only the lines affected by a content change.
 * Lines before the change are reused. Lines from the first dirty line onward
 * are retokenized until the end-state stabilizes.
 */
export function retokenizeRange(
  tokenLines: readonly TokenLine[],
  changedRange: Range,
  content: string,
  grammar: GrammarDefinition,
): TokenLine[] {
  const allLines = content.split('\n');
  const firstDirty = findFirstDirtyLine(tokenLines, changedRange);
  const result: TokenLine[] = [];

  for (let i = 0; i < firstDirty && i < tokenLines.length; i++) {
    result.push(tokenLines[i]!);
  }

  let state: TokenizerState = firstDirty > 0 && firstDirty <= tokenLines.length
    ? tokenLines[firstDirty - 1]!.state
    : createInitialState(grammar.scopeName as unknown as LanguageId);

  // Retokenize from the first dirty line until state stabilizes or we reach the end
  let lastAffected = findLastAffectedLine(tokenLines, firstDirty, grammar);
  if (lastAffected >= allLines.length) {
    lastAffected = allLines.length - 1;
  }

  for (let i = firstDirty; i < allLines.length; i++) {
    const lineResult = tokenizeLine(allLines[i]!, state, grammar);
    const newTokenLine: TokenLine = {
      lineNumber: i,
      tokens: lineResult.tokens,
      state,
      version: (tokenLines[i]?.version ?? 0) + 1,
    };
    result.push(newTokenLine);

    const oldEndState = i < tokenLines.length ? tokenLines[i]!.state : null;
    state = lineResult.endState;

    if (i > lastAffected && oldEndState !== null && stateEquals(state, oldEndState)) {
      for (let j = i + 1; j < tokenLines.length; j++) {
        result.push({
          ...tokenLines[j]!,
          lineNumber: j + (allLines.length - tokenLines.length),
        } as TokenLine);
      }
      break;
    }
  }

  return result;
}

/**
 * Create the initial tokenizer state for a given language.
 */
export function createInitialState(languageId: LanguageId): TokenizerState {
  return {
    ruleStack: [`source.${languageId as string}`],
    languageId,
  };
}

// ═══ §3 SCOPE RESOLUTION — scope hierarchy and token type mapping ═══

/**
 * Resolve a list of scopes against a grammar, returning the most specific
 * matching pattern from the repository.
 */
export function resolveScopes(
  scopes: readonly string[],
  grammar: GrammarDefinition,
): GrammarPattern | null {
  for (let i = scopes.length - 1; i >= 0; i--) {
    const scopeName = scopes[i]!;
    const parts = scopeName.split('.');
    for (let j = parts.length; j > 0; j--) {
      const candidate = parts.slice(0, j).join('.');
      if (grammar.repository[candidate] !== undefined) {
        return grammar.repository[candidate];
      }
    }
  }
  return null;
}

/**
 * Map a scope stack to a TokenType for syntax highlighting.
 */
export function scopeToTokenType(scopes: readonly string[]): TokenType {
  for (let i = scopes.length - 1; i >= 0; i--) {
    const scope = scopes[i]!;
    if (scope.startsWith('keyword')) return 'keyword';
    if (scope.startsWith('string')) return 'string';
    if (scope.startsWith('comment')) return 'comment';
    if (scope.startsWith('constant.numeric') || scope.startsWith('number')) return 'number';
    if (scope.startsWith('constant.regexp') || scope.startsWith('regexp')) return 'regex';
    if (scope.startsWith('entity.name.type') || scope.startsWith('support.type') || scope.startsWith('storage.type')) return 'type';
    if (scope.startsWith('entity.name.function') || scope.startsWith('support.function')) return 'identifier';
    if (scope.startsWith('variable') || scope.startsWith('entity.name')) return 'identifier';
    if (scope.startsWith('punctuation')) return 'punctuation';
    if (scope.startsWith('keyword.operator') || scope.startsWith('operator')) return 'operator';
    if (scope.startsWith('meta.decorator') || scope.startsWith('entity.name.decorator')) return 'decorator';
    if (scope.startsWith('meta.template') || scope.startsWith('string.template')) return 'template';
    if (scope.startsWith('meta.embedded') || scope.startsWith('embedded')) return 'embedded';
    if (scope.startsWith('invalid')) return 'invalid';
  }
  return 'identifier';
}

interface ScopeTreeNode {
  readonly scope: string;
  readonly startOffset: number;
  readonly endOffset: number;
  readonly children: ScopeTreeNode[];
}

/**
 * Build a hierarchical scope tree from a flat token array.
 * Tokens with nested scopes produce parent/child relationships.
 */
export function buildScopeTree(tokens: readonly Token[]): ScopeTreeNode {
  const root: ScopeTreeNode = {
    scope: 'root',
    startOffset: tokens.length > 0 ? tokens[0]!.startOffset : 0,
    endOffset: tokens.length > 0 ? tokens[tokens.length - 1]!.endOffset : 0,
    children: [],
  };

  const stack: { node: ScopeTreeNode; depth: number }[] = [{ node: root, depth: 0 }];

  for (const token of tokens) {
    for (let s = 0; s < token.scopes.length; s++) {
      const scope = token.scopes[s]!;
      if (s + 1 >= stack.length) {
        const newNode: ScopeTreeNode = {
          scope,
          startOffset: token.startOffset,
          endOffset: token.endOffset,
          children: [],
        };
        const parent = stack[stack.length - 1]!;
        (parent.node.children as ScopeTreeNode[]).push(newNode);
        stack.push({ node: newNode, depth: s + 1 });
      } else if (stack[s + 1]!.node.scope !== scope) {
        stack.length = s + 1;
        const newNode: ScopeTreeNode = {
          scope,
          startOffset: token.startOffset,
          endOffset: token.endOffset,
          children: [],
        };
        const parent = stack[stack.length - 1]!;
        (parent.node.children as ScopeTreeNode[]).push(newNode);
        stack.push({ node: newNode, depth: s + 1 });
      } else {
        const existing = stack[s + 1]!.node;
        (existing as { endOffset: number }).endOffset = token.endOffset;
      }
    }

    if (token.scopes.length < stack.length - 1) {
      stack.length = token.scopes.length + 1;
    }
  }

  return root;
}

/**
 * Find the deepest scope at a given offset within the scope tree.
 */
export function findScopeAt(scopeTree: ScopeTreeNode, offset: number): ScopeTreeNode | null {
  if (offset < scopeTree.startOffset || offset >= scopeTree.endOffset) {
    return null;
  }

  for (const child of scopeTree.children) {
    const found = findScopeAt(child, offset);
    if (found !== null) {
      return found;
    }
  }

  return scopeTree;
}

/**
 * Check whether one scope string contains another (i.e., outer is a prefix of inner).
 */
export function scopeContains(outer: string, inner: string): boolean {
  if (outer === inner) return true;
  return inner.startsWith(outer + '.');
}

// ═══ §4 PATTERN MATCHING — regex-based rule matching against text ═══

interface MatchInfo {
  readonly index: number;
  readonly length: number;
  readonly text: string;
}

/**
 * Attempt to match a grammar pattern at a given offset in the text.
 */
export function matchRule(
  text: string,
  offset: number,
  pattern: GrammarPattern,
): MatchInfo | null {
  const regexStr = pattern.match ?? pattern.begin;
  if (regexStr === undefined) return null;

  let regex: RegExp;
  try {
    regex = new RegExp(regexStr, 'g');
  } catch {
    return null;
  }
  regex.lastIndex = offset;
  const m = regex.exec(text);
  if (m === null || m.index !== offset) return null;

  return {
    index: m.index,
    length: m[0].length,
    text: m[0],
  };
}

/**
 * Given a match result and grammar captures, produce tokens for each named capture.
 */
export function matchCaptures(
  text: string,
  match: MatchInfo,
  captures: GrammarCaptures,
): Token[] {
  const tokens: Token[] = [];
  const regexStr = text.slice(match.index, match.index + match.length);

  for (const [captureId, captureDef] of Object.entries(captures)) {
    const idx = parseInt(captureId, 10);
    if (idx === 0) {
      tokens.push({
        type: scopeToTokenType([captureDef.name]),
        startOffset: match.index,
        endOffset: match.index + match.length,
        scopes: [captureDef.name],
      });
    } else {
      const captureRegex = new RegExp(regexStr, 'g');
      captureRegex.lastIndex = 0;
      const captureMatch = captureRegex.exec(text.slice(match.index, match.index + match.length));
      if (captureMatch && captureMatch[idx] !== undefined) {
        const capturedText = captureMatch[idx];
        const capturedStart = text.indexOf(capturedText, match.index);
        const start = capturedStart >= match.index ? capturedStart : match.index;
        tokens.push({
          type: scopeToTokenType([captureDef.name]),
          startOffset: start,
          endOffset: start + capturedText.length,
          scopes: [captureDef.name],
        });
      }
    }
  }

  return tokens;
}

/**
 * Apply a begin/end pattern at the given offset. If the begin matches,
 * scan for the end within the same line, pushing/popping state accordingly.
 */
export function applyBeginEnd(
  text: string,
  offset: number,
  pattern: GrammarPattern,
  state: { ruleStack: string[]; languageId: LanguageId },
): {
  tokens: Token[];
  newOffset: number;
  pushedScope: string | null;
} {
  if (pattern.begin === undefined || pattern.end === undefined) {
    return { tokens: [], newOffset: offset, pushedScope: null };
  }

  let beginRegex: RegExp;
  try {
    beginRegex = new RegExp(pattern.begin, 'g');
  } catch {
    return { tokens: [], newOffset: offset, pushedScope: null };
  }
  beginRegex.lastIndex = offset;
  const beginMatch = beginRegex.exec(text);
  if (beginMatch === null || beginMatch.index !== offset) {
    return { tokens: [], newOffset: offset, pushedScope: null };
  }

  const tokens: Token[] = [];
  const scopeName = pattern.name ?? pattern.contentName ?? 'meta.block';
  const scopes = [...state.ruleStack, scopeName];

  if (pattern.beginCaptures) {
    const capTokens = matchCaptures(text, {
      index: beginMatch.index,
      length: beginMatch[0].length,
      text: beginMatch[0],
    }, pattern.beginCaptures);
    for (const ct of capTokens) {
      tokens.push(ct);
    }
  } else {
    tokens.push({
      type: scopeToTokenType(scopes),
      startOffset: beginMatch.index,
      endOffset: beginMatch.index + beginMatch[0].length,
      scopes,
    });
  }

  let endRegex: RegExp;
  try {
    endRegex = new RegExp(pattern.end, 'g');
  } catch {
    return {
      tokens,
      newOffset: beginMatch.index + beginMatch[0].length,
      pushedScope: scopeName,
    };
  }

  const contentStart = beginMatch.index + beginMatch[0].length;
  endRegex.lastIndex = contentStart;
  const endMatch = endRegex.exec(text);

  if (endMatch === null) {
    if (contentStart < text.length) {
      const contentScopes = pattern.contentName
        ? [...state.ruleStack, pattern.contentName]
        : scopes;
      tokens.push({
        type: scopeToTokenType(contentScopes),
        startOffset: contentStart,
        endOffset: text.length,
        scopes: contentScopes,
      });
    }
    return {
      tokens,
      newOffset: text.length,
      pushedScope: scopeName,
    };
  }

  if (endMatch.index > contentStart) {
    const contentScopes = pattern.contentName
      ? [...state.ruleStack, pattern.contentName]
      : scopes;
    tokens.push({
      type: scopeToTokenType(contentScopes),
      startOffset: contentStart,
      endOffset: endMatch.index,
      scopes: contentScopes,
    });
  }

  if (pattern.endCaptures) {
    const endCapTokens = matchCaptures(text, {
      index: endMatch.index,
      length: endMatch[0].length,
      text: endMatch[0],
    }, pattern.endCaptures);
    for (const ct of endCapTokens) {
      tokens.push(ct);
    }
  } else {
    tokens.push({
      type: scopeToTokenType(scopes),
      startOffset: endMatch.index,
      endOffset: endMatch.index + endMatch[0].length,
      scopes,
    });
  }

  return {
    tokens,
    newOffset: endMatch.index + endMatch[0].length,
    pushedScope: null,
  };
}

interface FirstMatchResult {
  readonly offset: number;
  readonly matchLength: number;
  readonly matchText: string;
  readonly pattern: GrammarPattern;
}

/**
 * Scan all patterns and return the one that matches earliest in the text.
 * Ties are broken by pattern order (first declared wins).
 */
export function findFirstMatch(
  text: string,
  offset: number,
  patterns: readonly GrammarPattern[],
): FirstMatchResult | null {
  let best: FirstMatchResult | null = null;

  for (const pattern of patterns) {
    const regexStr = pattern.match ?? pattern.begin;
    if (regexStr === undefined) continue;

    let regex: RegExp;
    try {
      regex = new RegExp(regexStr, 'g');
    } catch {
      continue;
    }
    regex.lastIndex = offset;
    const m = regex.exec(text);
    if (m === null) continue;

    if (best === null || m.index < best.offset) {
      best = {
        offset: m.index,
        matchLength: m[0].length,
        matchText: m[0],
        pattern,
      };
    }
  }

  return best;
}

// ═══ §5 STATE MANAGEMENT — tokenizer state stack operations ═══

/**
 * Push a new scope onto the tokenizer state stack.
 */
export function pushState(
  state: TokenizerState,
  scopeName: string,
  _patterns: readonly GrammarPattern[],
): TokenizerState {
  return {
    ruleStack: [...state.ruleStack, scopeName],
    languageId: state.languageId,
  };
}

/**
 * Pop the top scope from the tokenizer state stack.
 * Returns the original state if the stack has only one entry.
 */
export function popState(state: TokenizerState): TokenizerState {
  if (state.ruleStack.length <= 1) return state;
  return {
    ruleStack: state.ruleStack.slice(0, -1),
    languageId: state.languageId,
  };
}

/**
 * Return the top scope of the state stack without modifying it.
 */
export function peekState(state: TokenizerState): string {
  return state.ruleStack[state.ruleStack.length - 1] ?? '';
}

/**
 * Compare two tokenizer states for equality.
 */
export function stateEquals(a: TokenizerState, b: TokenizerState): boolean {
  if (a.languageId !== b.languageId) return false;
  if (a.ruleStack.length !== b.ruleStack.length) return false;
  for (let i = 0; i < a.ruleStack.length; i++) {
    if (a.ruleStack[i] !== b.ruleStack[i]) return false;
  }
  return true;
}

/**
 * Serialize a tokenizer state to a JSON-safe format for persistence or transfer.
 */
export function serializeState(state: TokenizerState): string {
  return JSON.stringify({
    ruleStack: state.ruleStack,
    languageId: state.languageId,
  });
}

/**
 * Deserialize a tokenizer state from its serialized form.
 */
export function deserializeState(data: string): EditorResult<TokenizerState> {
  try {
    const parsed = JSON.parse(data) as { ruleStack: string[]; languageId: string };
    if (!Array.isArray(parsed.ruleStack) || typeof parsed.languageId !== 'string') {
      return err('INVALID_STATE', 'Invalid serialized tokenizer state');
    }
    return ok({
      ruleStack: parsed.ruleStack,
      languageId: asLanguageId(parsed.languageId),
    });
  } catch (e) {
    return err('PARSE_ERROR', 'Failed to parse tokenizer state', e);
  }
}

// ═══ §6 OPTIMIZATION — dirty line detection and caching ═══

/**
 * Determine whether retokenization is needed by comparing old and new states.
 */
export function shouldRetokenize(oldState: TokenizerState, newState: TokenizerState): boolean {
  return !stateEquals(oldState, newState);
}

/**
 * Find the first line index that needs retokenization based on an edit range.
 */
export function findFirstDirtyLine(
  tokenLines: readonly TokenLine[],
  editRange: Range,
): number {
  const startLine = editRange.start.line;
  if (startLine <= 0) return 0;
  if (startLine >= tokenLines.length) return tokenLines.length;
  return startLine;
}

/**
 * Find the last line that could be affected by a change starting at startLine.
 * This is where the state produced after retokenization stabilizes back to
 * the original state, meaning subsequent lines don't need retokenization.
 */
export function findLastAffectedLine(
  tokenLines: readonly TokenLine[],
  startLine: number,
  _grammar: GrammarDefinition,
): number {
  if (tokenLines.length === 0) return 0;

  const baseState = startLine > 0 && startLine < tokenLines.length
    ? tokenLines[startLine - 1]!.state
    : null;

  let lastAffected = startLine;
  for (let i = startLine; i < tokenLines.length; i++) {
    lastAffected = i;
    if (i > startLine + 1) {
      const prevState = i > 0 ? tokenLines[i - 1]!.state : null;
      if (prevState !== null && baseState !== null && stateEquals(prevState, baseState)) {
        break;
      }
    }
  }

  return Math.min(lastAffected + 10, tokenLines.length - 1);
}

/** Maximum number of cached token line entries. */
export const TOKEN_CACHE_SIZE = 2048;

/**
 * Estimate memory usage in bytes for an array of token lines.
 */
export function estimateTokenMemory(tokenLines: readonly TokenLine[]): number {
  let bytes = 0;
  for (const tl of tokenLines) {
    bytes += 64; // overhead per TokenLine (lineNumber, version, state ref)
    bytes += tl.state.ruleStack.length * 48; // avg scope string
    for (const token of tl.tokens) {
      bytes += 40; // type + offsets
      bytes += token.scopes.length * 48; // scope strings
    }
  }
  return bytes;
}

// ═══ INTERNAL HELPERS ═══

function resolveActivePatterns(
  ruleStack: readonly string[],
  grammar: GrammarDefinition,
): GrammarPattern[] {
  const topScope = ruleStack[ruleStack.length - 1] ?? '';
  const repoPattern = grammar.repository[topScope];
  if (repoPattern !== undefined && repoPattern.patterns !== undefined) {
    return repoPattern.patterns;
  }
  return grammar.patterns;
}

function scopeStackToTokenType(ruleStack: readonly string[]): TokenType {
  return scopeToTokenType(ruleStack);
}
